import { type ClassValue, clsx } from "clsx";
import dayjs from "dayjs";
import {
  ALargeSmall,
  Code,
  FileText,
  Image as ImageIcon,
  MessageSquare,
  Palette,
  Sparkles,
  Tag,
  Target,
  Text,
  Type,
} from "lucide-react";
import { twMerge } from "tailwind-merge";
import { CONTENT_TYPES } from "@/lib/constants/content-structure";
import type {
  ApplyTo,
  AutopilotField,
  AutopilotFieldRule,
  ContentStructureRuleFormData,
  ContentType,
  Instruction,
  LengthUnit,
  MustIncludeOption,
} from "@/types/autopilot";
import type {
  SearchVisibilityData,
  SearchVisibilityResponse,
} from "@/types/monitoring";
import type { UIMode } from "@/types/user";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function camelize(str: string): string {
  return str
    ?.replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""))
    ?.replace(/^(.)/, (m) => m.toLowerCase());
}

/**
 * Extracts a human-readable message from an unknown API error.
 *
 * Probes three shapes the backend may return:
 *  1. `{ data: { message: string } }` — nested response wrapper
 *  2. `{ message: string }` — top-level message
 *  3. Plain string
 *
 * Returns `"Unknown error"` if none match.
 *
 * @see .issues/tech-debt/api-error-typing.md for plans to replace this with a typed error contract.
 */
export const handleApiError = (error: unknown): string => {
  let errorMessage = "Unknown error";
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    error.data &&
    typeof error.data === "object" &&
    "message" in error.data
  ) {
    errorMessage = String(error.data.message);
  } else if (error && typeof error === "object" && "message" in error) {
    errorMessage = String(error.message);
  } else if (typeof error === "string") {
    errorMessage = error;
  }
  return errorMessage;
};

export function formatNumber(number: number) {
  if (!number) return "0";
  return number.toLocaleString("en-US");
}

export const parseUIMode = (mode: UIMode) => {
  let preferredTheme: UIMode = mode;
  if (mode === "system") {
    const userPrefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    preferredTheme = userPrefersDark ? "dark" : "light";
  }
  return preferredTheme;
};

const isHigh = (percentage: number) => percentage >= 80;
const isMedium = (percentage: number) => percentage >= 40;

const getByPercentage = (
  percentage: number,
  high: string,
  medium: string,
  low: string,
) => (isHigh(percentage) ? high : isMedium(percentage) ? medium : low);

/** Returns a Tailwind text color class for a score percentage.
 *  >= 80 → primary (good), >= 40 → chart-2 (moderate), < 40 → destructive (poor) */
export const getColorByPercentage = (percentage: number) =>
  getByPercentage(
    percentage,
    "text-primary",
    "text-chart-2",
    "text-destructive",
  );

/** Returns a Tailwind background color class for a score percentage.
 *  >= 80 → primary (good), >= 40 → warning (moderate), < 40 → destructive (poor) */
export const getBgColorByPercentage = (percentage: number) =>
  getByPercentage(percentage, "bg-primary", "bg-warning", "bg-destructive");

/** Returns a Tailwind border color class for a score percentage.
 *  >= 80 → primary (good), >= 40 → warning (moderate), < 40 → destructive (poor) */
export const getBorderColorByPercentage = (percentage: number) =>
  getByPercentage(
    percentage,
    "border-primary",
    "border-warning",
    "border-destructive",
  );

export const getFullBorderColorByPercentage = (percentage: number) =>
  getByPercentage(
    percentage,
    "border-primary/50",
    "border-warning/50",
    "border-destructive/50",
  );

export const getIconColorByPercentage = (percentage: number) =>
  getByPercentage(
    percentage,
    "text-primary",
    "text-chart-2",
    "text-destructive",
  );

export const getMonitoringBgColorByPercentage = (percentage: number) =>
  getByPercentage(percentage, "bg-primary", "bg-warning", "bg-destructive");

export const getCardBgByPercentage = (percentage: number) =>
  getByPercentage(
    percentage,
    "bg-primary/5",
    "bg-warning/5",
    "bg-destructive/5",
  );

export const getCardBgHoverByPercentage = (percentage: number) =>
  getByPercentage(
    percentage,
    "hover:bg-primary/10",
    "hover:bg-warning/10",
    "hover:bg-destructive/10",
  );

export const getHoverBorderColorByPercentage = (percentage: number) =>
  getByPercentage(
    percentage,
    "hover:border-primary",
    "hover:border-warning",
    "hover:border-destructive",
  );

export function formatDate(dateString: string | undefined): string {
  return dateString
    ? dayjs(dateString).format("MMM D, YYYY")
    : dayjs().format("MMM D, YYYY");
}

export function calculateTotalPages(count: number, pageSize: number): number {
  return Math.ceil(count / pageSize);
}

/** Sorts team members so that invited members appear last. */
export function sortTeamMembersByStatus<
  T extends { team_member_status?: string },
>(members: T[]): T[] {
  return [...members].sort((a, b) =>
    a.team_member_status === "invited"
      ? 1
      : b.team_member_status === "invited"
        ? -1
        : 0,
  );
}

export const formatCurrency = (amount: string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(parseFloat(amount));
};

export const formatToTwoDecimals = (
  value: number | null | undefined,
): string => {
  const numberValue = Number(value) || 0;
  return numberValue.toFixed(2);
};

export const getFieldConfig = (t: (key: string) => string) => ({
  alt_text: {
    description: t("alt_text.description"),
    tooltip: t("tooltips.alt_text"),
    icon: <ImageIcon className="h-5 w-5" />,
    riskLevel: "low" as const,
    iconBgColor: "bg-secondary/10",
    iconColor: "text-secondary",
  },
  seo_description: {
    description: t("seo_description.description"),
    tooltip: t("tooltips.seo_description"),
    icon: <FileText className="h-5 w-5" />,
    riskLevel: "low" as const,
    iconBgColor: "bg-teal-500/10",
    iconColor: "text-teal-500",
  },
  seo_title: {
    description: t("seo_title.description"),
    tooltip: t("tooltips.seo_title"),
    icon: <Type className="h-5 w-5" />,
    riskLevel: "medium" as const,
    iconBgColor: "bg-primary/10",
    iconColor: "text-primary",
  },
  json_ld: {
    description: t("json_ld.description"),
    tooltip: t("tooltips.json_ld"),
    icon: <Code className="h-5 w-5" />,
    riskLevel: "low" as const,
    iconBgColor: "bg-muted",
    iconColor: "text-muted-foreground",
  },
  title: {
    description: t("title.description"),
    tooltip: t("tooltips.title"),
    icon: <Type className="h-5 w-5" />,
    riskLevel: "medium" as const,
    iconBgColor: "bg-secondary/10",
    iconColor: "text-secondary",
  },
  description_html: {
    description: t("description_html.description"),
    tooltip: t("tooltips.description_html"),
    icon: <FileText className="h-5 w-5" />,
    riskLevel: "medium" as const,
    iconBgColor: "bg-primary/10",
    iconColor: "text-primary",
  },
  description_details: {
    description: t("description_html.description"),
    tooltip: t("tooltips.description_html"),
    icon: <FileText className="h-5 w-5" />,
    riskLevel: "medium" as const,
    iconBgColor: "bg-primary/10",
    iconColor: "text-primary",
  },
  short_description: {
    description: t("description_html.description"),
    tooltip: t("tooltips.description_html"),
    icon: <Text className="h-5 w-5" />,
    riskLevel: "low" as const,
    iconBgColor: "bg-teal-500/10",
    iconColor: "text-teal-500",
  },
  product_name: {
    description: t("title.description"),
    tooltip: t("tooltips.title"),
    icon: <ALargeSmall className="h-5 w-5" />,
    riskLevel: "medium" as const,
    iconBgColor: "bg-secondary/10",
    iconColor: "text-secondary",
  },
  name: {
    description: t("title.description"),
    tooltip: t("tooltips.title"),
    icon: <ALargeSmall className="h-5 w-5" />,
    riskLevel: "medium" as const,
    iconBgColor: "bg-secondary/10",
    iconColor: "text-secondary",
  },
  tags: {
    description: t("tags.description"),
    tooltip: t("tooltips.tags"),
    icon: <Tag className="h-5 w-5" />,
    riskLevel: "low" as const,
    iconBgColor: "bg-muted",
    iconColor: "text-muted-foreground",
  },
});

export function formatCompactNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }
  return num.toString();
}

export function formatPercentage(decimal: number, decimals = 2): string {
  const percentage = decimal * 100;
  const formatted =
    percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(decimals);
  return `${formatted}%`;
}

export function formatChangePercentage(change: number): string {
  const sign = change > 0 ? "+" : "";
  const formatted = change % 1 === 0 ? change.toFixed(0) : change.toFixed(2);
  return `${sign}${formatted}%`;
}

export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function formatPosition(position: number): string {
  return position.toFixed(1);
}

export function calculatePercentage(part: number, total: number): string {
  if (total === 0) return "0%";
  const percentage = (part / total) * 100;
  const formatted =
    percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(2);
  return `${formatted}%`;
}

export function transformSearchVisibilityData(
  backendData: SearchVisibilityResponse,
): SearchVisibilityData {
  const { current_period, difference } = backendData;

  return {
    stats: {
      searchImpressions: {
        total: current_period.impressions,
        changePercentage: difference.impressions.percentage,
        totalClicks: current_period.clicks,
      },
      organicCTR: {
        ctr: current_period.ctr_pct / 100,
        changePercentage: difference.ctr_pct.percentage,
        avgPosition: current_period.avg_position,
      },
      indexCoverage: {
        indexed: backendData.indexation_data.indexed_products,
        total: backendData.indexation_data.total_products,
        errors: 0, // Not provided by backend
        excluded: backendData.indexation_data.not_indexed_products,
      },
      organicSessions: {
        total: current_period.sessions,
        changePercentage: difference.sessions.percentage,
      },
      organicUsers: {
        total: current_period.sessions,
        userPercentage: 100 - current_period.new_users_pct,
        newUsersPercentage: current_period.new_users_pct,
      },
      bounceRate: {
        rate: current_period.bounce_rate_pct / 100,
        changePercentage: difference.bounce_rate_pct.percentage,
        pagesPerSession: current_period.pages_per_session,
      },
    },
    searchPerformance: {
      totalImpressions: current_period.impressions,
      impressionsChangePercentage: difference.impressions.percentage,
      totalClicks: current_period.clicks,
      clicksChangePercentage: difference.clicks.percentage,
      clickThroughRate: current_period.ctr_pct / 100,
      avgPosition: current_period.avg_position,
    },
    organicTrafficOverview: {
      sessions: current_period.sessions,
      sessionsChangePercentage: difference.sessions.percentage,
      pageviews: current_period.pageviews,
      pagesPerSessionRatio: current_period.pages_per_session,
      avgSessionDuration: current_period.avg_session_duration_seconds,
      newVsReturningPercentage: current_period.new_users_pct,
      pagesPerSession: current_period.pages_per_session,
    },
    topSearchQueries: backendData.top_gsc_queries.top_pages.map((query) => ({
      query: query.page,
      impressions: query.impressions,
      clicks: query.clicks,
      position: query.avg_position,
    })),
    topLandingPages: backendData.top_landing_pages.map((page) => ({
      url: page.page_path,
      sessions: page.sessions,
      bounceRate: page.bounce_rate_pct / 100,
      avgTime: page.avg_session_duration_seconds,
    })),
    indexCoverageStatus: {
      validPages: backendData.indexation_data.indexed_products,
      withWarnings: 0,
      errors: backendData.indexation_data.not_indexed_products,
    },
    topPerformingPages: backendData.top_gsc_pages.top_pages.map((page) => ({
      url: page.page,
      impressions: page.impressions,
      clicks: page.clicks,
      position: page.avg_position,
    })),
    avgCTR: current_period.ctr_pct / 100,
  };
}

export const capitalizeFirst = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getRuleTypeConfig = (ruleType: string) => {
  const configs = {
    custom: {
      icon: Sparkles,
      bg: "bg-orange-500",
      bgLight: "bg-orange-500/10",
      text: "text-orange-500",
    },
    audience: {
      icon: Target,
      bg: "bg-blue-500",
      bgLight: "bg-blue-500/10",
      text: "text-blue-500",
    },
    tone: {
      icon: Palette,
      bg: "bg-purple-500",
      bgLight: "bg-purple-500/10",
      text: "text-purple-500",
    },
    message: {
      icon: MessageSquare,
      bg: "bg-green-500",
      bgLight: "bg-green-500/10",
      text: "text-green-500",
    },
  };

  return (
    configs[ruleType.toLowerCase() as keyof typeof configs] || configs.custom
  );
};
export function arraysEqual(a: string[] = [], b: string[] = []) {
  if (a.length !== b.length) return false;
  return a.every((val, idx) => val === b[idx]);
}

// Build the `custom`-rule entries that should be persisted for a given list
// of in-memory Instruction rows. Pure helper, called from the save handler.
//
// Critically: this filter must stay symmetric with extractInstructionsFromRules
// below. Both sides drop entries whose `text` is empty / whitespace-only.
// If the save side persisted an empty-body row, the read side would silently
// drop it on reload, and the next save would permanently delete it from the
// backend because the in-memory model no longer contained it (round-trip
// data loss).
export function instructionsToRules(
  instructions: readonly Instruction[],
): AutopilotFieldRule[] {
  const rules: AutopilotFieldRule[] = [];
  for (const instruction of instructions) {
    if (!instruction.text.trim()) continue;
    const categoriesIdentifiers =
      instruction.applyTo === "specified-categories"
        ? instruction.selectedCategories
        : [];
    rules.push({
      rule_type: "custom",
      title: instruction.title?.trim() || null,
      description: null,
      bullets: [],
      categories_identifiers: categoriesIdentifiers,
      max_length: null,
      min_length: null,
      max_length_unit: null,
      must_be: [],
      must_be_delimiter: null,
      must_include: [],
      text: instruction.text,
      required_elements: [],
      forbidden_elements: [],
    } as AutopilotFieldRule);
  }
  return rules;
}

export function extractInstructionsFromRules(
  field: AutopilotField,
): Instruction[] {
  const rulesList = field.content_structure_rules_list || [];
  const instructions: Instruction[] = [];

  // IDs are derived from the field name + the rule's index in the field's
  // rule list. This keeps them stable across re-renders, which matters for
  // React keys in display components (the card calls this on every render).
  // The dialog wraps the result in formData state and assigns fresh random
  // IDs (via newId()) for any rows the user adds locally, so collisions
  // between deterministic + random IDs are not possible.
  rulesList.forEach((rule, ruleIndex) => {
    const ruleCategories = rule.categories_identifiers ?? [];
    const ruleApplyTo: ApplyTo =
      ruleCategories.length > 0 ? "specified-categories" : "all-products";

    if (rule.rule_type === "custom") {
      if (rule.text) {
        instructions.push({
          id: `${field.name}-rule-${ruleIndex}`,
          title: rule.title || undefined,
          text: rule.text,
          applyTo: ruleApplyTo,
          selectedCategories: ruleCategories,
        });
      }
    } else if (
      ![
        "content_length",
        "must_include",
        "required_elements",
        "forbidden_elements",
      ].includes(rule.rule_type) &&
      rule.bullets
    ) {
      rule.bullets.forEach((bullet, bulletIndex) => {
        instructions.push({
          id: `${field.name}-rule-${ruleIndex}-bullet-${bulletIndex}`,
          text: bullet,
          applyTo: ruleApplyTo,
          selectedCategories: ruleCategories,
        });
      });
    }
  });

  return instructions;
}

export function extractContentLengthFromRules(field: AutopilotField): {
  min: string;
  max: string;
  unit: LengthUnit;
} {
  const rulesList = field.content_structure_rules_list || [];
  const lengthRule = rulesList.find((r) => r.rule_type === "content_length");

  return {
    min: lengthRule?.min_length?.toString() || "",
    max: lengthRule?.max_length?.toString() || "",
    unit: (lengthRule?.max_length_unit as LengthUnit) || "characters",
  };
}

export function extractMustIncludeFromRules(field: AutopilotField): {
  options: MustIncludeOption[];
  custom: string;
} {
  const rulesList = field.content_structure_rules_list || [];
  const mustIncludeRule = rulesList.find((r) => r.rule_type === "must_include");
  const mustIncludeArray = mustIncludeRule?.must_include || [];

  const options: MustIncludeOption[] = [];
  let custom = "";

  for (const item of mustIncludeArray) {
    if (item.must_include_type === "Title") {
      options.push("title");
    } else if (item.must_include_type === "Features and Benefits") {
      options.push("features-and-benefits");
    } else if (item.must_include_type === "Q and A Section") {
      options.push("q-and-a");
    } else if (item.must_include_type === "Custom" && item.text) {
      options.push("custom");
      custom = item.text;
    }
  }

  return { options, custom };
}

export function fieldToFormData(
  field: AutopilotField,
): ContentStructureRuleFormData {
  const contentLength = extractContentLengthFromRules(field);
  const mustInclude = extractMustIncludeFromRules(field);

  return {
    contentType: field.name as ContentType,
    instructionsForAI: extractInstructionsFromRules(field),
    contentLengthMin: contentLength.min || "",
    contentLengthMax: contentLength.max || "",
    contentLengthUnit: contentLength.unit,
    mustInclude: mustInclude.options,
    mustIncludeCustom: mustInclude.custom,
    mustBeOneOf: "",
    forbiddenElements: getAllRequiredAndForbiddenElements(
      field,
      "forbidden_elements",
    ),
    requiredElements: getAllRequiredAndForbiddenElements(
      field,
      "required_elements",
    ),
  };
}

export function createEmptyFormData(): ContentStructureRuleFormData {
  return {
    contentType: "seo_title",
    instructionsForAI: [],
    contentLengthMin: "",
    contentLengthMax: "",
    contentLengthUnit: "characters",
    mustInclude: [],
    mustIncludeCustom: "",
    mustBeOneOf: "",
    forbiddenElements: [],
    requiredElements: [],
  };
}

export const getContentLengthInfo = (field: AutopilotField) => {
  const lengthRules: Array<{
    min: number | null;
    max: number | null;
    unit: string | null;
  }> = [];

  if (field.content_structure_rules_list) {
    field.content_structure_rules_list.forEach((ruleItem) => {
      if (
        ruleItem.rule_type === "content_length" &&
        (ruleItem.min_length || ruleItem.max_length)
      ) {
        lengthRules.push({
          min: ruleItem.min_length,
          max: ruleItem.max_length,
          unit: ruleItem.max_length_unit,
        });
      }
    });
  }
  return lengthRules;
};

export function toggleCategory(
  selectedCategories: string[],
  identifier: string,
): string[] {
  if (selectedCategories.includes(identifier)) {
    return selectedCategories.filter((cat) => cat !== identifier);
  }
  return [...selectedCategories, identifier];
}

export const getAllMustInclude = (field: AutopilotField) => {
  const mustIncludeMap = new Map<string, string | null>();
  if (field.content_structure_rules_list) {
    field.content_structure_rules_list.forEach((ruleItem) => {
      if (ruleItem.must_include && ruleItem.must_include.length > 0) {
        ruleItem.must_include.forEach((item) => {
          if (item.must_include_type === "Custom" && item.text) {
            mustIncludeMap.set(
              `${item.must_include_type}:${item.text}`,
              item.text,
            );
          } else {
            mustIncludeMap.set(item.must_include_type, null);
          }
        });
      }
    });
  }
  return Array.from(mustIncludeMap.entries()).map(([key, value]) => ({
    type: key.includes(":") ? key.split(":")[0] : key,
    text: value,
  }));
};

export const getAllRequiredAndForbiddenElements = (
  field: AutopilotField,
  key: "required_elements" | "forbidden_elements",
) => {
  const requiredElements: string[] = [];
  if (field.content_structure_rules_list) {
    field.content_structure_rules_list.forEach((ruleItem) => {
      if (ruleItem[key] && ruleItem[key].length > 0) {
        ruleItem[key].forEach((element) => {
          requiredElements.push(element);
        });
      }
    });
  }
  return requiredElements;
};

export const getAllBullets = (field: AutopilotField) => {
  const bullets: string[] = [];
  if (field.content_structure_rules_list) {
    field.content_structure_rules_list.forEach((ruleItem) => {
      if (ruleItem.bullets && ruleItem.bullets.length > 0) {
        bullets.push(...ruleItem.bullets);
      }
    });
  }
  return bullets;
};

// Maximum category names rendered inline in a scope badge before we collapse
// the rest into "+N". Used by both Content Structure and Brand Voice cards.
export const MAX_SCOPE_BADGE_NAMES = 3;

// How many characters of stripped body text to show as a snippet preview when
// an instruction has no title. Lives here so the dialog row preview and the
// card snippet stay roughly in sync if we ever want to tune them.
export const INSTRUCTION_SNIPPET_PREVIEW_CHARS = 120;

export type ScopeDescriptor =
  | { kind: "all-products" }
  | { kind: "specific-empty" }
  | { kind: "specific"; shown: string; remaining: number; full: string };

// Build a renderable description of an instruction's scope. The caller is
// responsible for passing in a memoized identifier→title lookup so we don't
// rebuild it per row.
export function describeScope(
  applyTo: ApplyTo,
  selectedCategoryIds: readonly string[],
  categoryTitleMap: ReadonlyMap<string, string>,
): ScopeDescriptor {
  if (applyTo !== "specified-categories") return { kind: "all-products" };
  if (selectedCategoryIds.length === 0) return { kind: "specific-empty" };
  const titles = selectedCategoryIds.map(
    (id) => categoryTitleMap.get(id) ?? id,
  );
  const shown = titles.slice(0, MAX_SCOPE_BADGE_NAMES).join(", ");
  const remaining = titles.length - MAX_SCOPE_BADGE_NAMES;
  return {
    kind: "specific",
    shown,
    remaining: Math.max(0, remaining),
    full: titles.join(", "),
  };
}

// Build a memoizable identifier→title lookup for shop categories. Cheap, but
// callers should still useMemo it so it isn't rebuilt on every render.
export function buildCategoryTitleMap(
  shopCategories: readonly { identifier: string; title: string }[],
): Map<string, string> {
  return new Map(shopCategories.map((c) => [c.identifier, c.title]));
}

// Strip the most common markdown syntax so a blob of markdown source can be
// shown as a plain-text preview (e.g. inside a line-clamped card header).
// Not a full parser — just enough to keep `### Heading`, `**bold**`, list
// markers, and table pipes from leaking into the UI as raw characters.
export const stripMarkdown = (input: string | null | undefined): string => {
  if (!input) return "";
  return input
    .replace(/```[\s\S]*?```/g, " ") // fenced code blocks
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1") // images
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // links
    .replace(/^#{1,6}\s+/gm, "") // headings
    .replace(/^\s*>\s?/gm, "") // blockquotes
    .replace(/^\s*[-*+]\s+/gm, "") // unordered list markers
    .replace(/^\s*\d+\.\s+/gm, "") // ordered list markers
    .replace(/\*\*([^*]+)\*\*/g, "$1") // bold
    .replace(/__([^_]+)__/g, "$1")
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "$1") // italic
    .replace(/(?<!_)_([^_]+)_(?!_)/g, "$1")
    .replace(/~~([^~]+)~~/g, "$1") // strikethrough
    .replace(/^\s*\|.*\|\s*$/gm, (line) =>
      line.replace(/\|/g, " ").replace(/-{2,}/g, " "),
    ) // table rows
    .replace(/\s+/g, " ")
    .trim();
};

export const getOptionLabel = (option: string, t: (key: string) => string) => {
  switch (option) {
    case "title":
      return t("title");
    case "features-and-benefits":
      return t("featuresAndBenefits");
    case "q-and-a":
      return t("qAndA");
    case "custom":
      return t("custom");
    default:
      return option;
  }
};

export const getPerformanceScoreTextColor = (score: number) => {
  if (score >= 80) return "text-primary";
  if (score >= 30) return "text-warning";
  return "text-destructive";
};

export const getPerformanceScoreBgColor = (score: number) => {
  if (score >= 80) return "bg-primary";
  if (score >= 30) return "bg-warning";
  return "bg-destructive";
};

export const getLoadTimeTextColor = (timeInSeconds: number) => {
  if (timeInSeconds < 3) return "text-primary";
  return "text-warning";
};

export const getComplianceBgColor = (percentage: number) => {
  if (percentage === 100) return "bg-primary";
  return "bg-warning";
};

export const getIconByName = (name: string) => {
  const type = CONTENT_TYPES.find((t) => t.value === name);
  if (type) {
    const Icon = type.icon;
    return <Icon className={`w-4 h-4 ${type.color}`} />;
  }
  return <FileText className="w-4 h-4 text-muted-foreground" />;
};

export const normalizeValue = (
  value: string | number | null | undefined,
): string | number => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" && value.trim() === "") return "";
  return value;
};

export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export const validateAdminAction = (role: string): boolean => {
  return role === "admin" || role === "review_admin";
};
